import React, { useRef, useState } from 'react';
import MouseMoveHandler from './MouseMoveHandler';

export interface ProgressBarProps {
  preProgress?: number;
  progress: number;
  strokeColor: string;
  vertical?: boolean;
  nodes?: 1 | 2;
  endLabel?: string;
  startLabel?: string;
  onChange: (max: number, min?: number) => void;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  preProgress = 0,
  progress,
  strokeColor,
  vertical = false,
  nodes = 1,
  startLabel,
  endLabel,
  onChange,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragInitialPosition, setDragInitialPosition] = useState([0, 0]);
  const [initialNode, setInitialNode] = useState(0);
  const [dragee, setDragee] = useState<'start' | 'end'>('end');

  const barRef = useRef<HTMLDivElement>(null);

  const setPreProgressBar = (val: number) => {
    if (!barRef.current) {
      return;
    }
    const bounds = barRef.current.getBoundingClientRect();
    const total = vertical ? barRef.current.clientHeight : barRef.current.clientWidth;
    const counter = (vertical ? -1 : 1) * (val - (vertical ? bounds.bottom : bounds.left));
    const newProgress = (Math.max(counter, 0) / total) * 100;
    onChange(progress, Math.min(newProgress, progress - 5));
  };

  const setProgressBar = (val: number) => {
    if (!barRef.current) {
      return;
    }
    const bounds = barRef.current.getBoundingClientRect();
    const total = vertical ? barRef.current.clientHeight : barRef.current.clientWidth;
    const counter = (vertical ? -1 : 1) * (val - (vertical ? bounds.bottom : bounds.left));
    const newProgress = Math.max((Math.max(counter, 0) / total) * 100, preProgress + 5);
    onChange(Math.min(newProgress, 100), preProgress);
  };

  const findProximateNode = (clientY: number, clientX: number) => {
    if (!barRef.current) {
      return 'end';
    }
    const val = vertical ? clientY : clientX;
    const bounds = barRef.current.getBoundingClientRect();
    const total = vertical ? barRef.current.clientHeight : barRef.current.clientWidth;
    const counter = (vertical ? -1 : 1) * (val - (vertical ? bounds.bottom : bounds.left));
    const point = (Math.max(counter, preProgress + 10) / total) * 100;
    const preDiv = Math.abs(preProgress - point);
    const div = Math.abs(point - progress);

    return div > preDiv ? 'start' : 'end';
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    const proximate = findProximateNode(event.clientY, event.clientX);
    setDragee(proximate);
    setIsDragging(true);
    setDragInitialPosition([event.clientX, event.clientY]);
    if (proximate === 'end') {
      setInitialNode(progress);
      setProgressBar(vertical ? event.clientY : event.clientX);
    } else {
      setInitialNode(preProgress);
      setPreProgressBar(vertical ? event.clientY : event.clientX);
    }
  };

  const onMouseMove = (event: MouseEvent) => {
    if (!barRef.current || !isDragging) {
      return;
    }
    const totalLength = vertical ? barRef.current.clientHeight : barRef.current.clientWidth;
    if (totalLength === 0) {
      return;
    }
    let divPercentage = 0;

    if (vertical) {
      (divPercentage = Math.max((100 * (event.clientY - dragInitialPosition[1])) / totalLength)), 100;
    } else {
      (divPercentage = Math.max((100 * (event.clientX - dragInitialPosition[0])) / totalLength)), 100;
    }
    if (dragee === 'end') {
      const newProgress = Math.max(initialNode + divPercentage, preProgress + 5);
      onChange(Math.min(newProgress, 100), preProgress);
    } else {
      const newPreProgress = Math.max(initialNode + divPercentage, 0);
      onChange(progress, Math.min(newPreProgress, progress - 5));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragInitialPosition([0, 0]);
  };

  return (
    <MouseMoveHandler onMove={onMouseMove} onMouseUp={handleMouseUp} className="w-full">
      <div
        ref={barRef}
        className={`flex cursor-pointer items-center rounded-full bg-primary-cloud ${
          vertical ? 'h-full w-4 flex-col-reverse' : 'lex-row mx-16 h-4'
        } `}
        onMouseDown={handleMouseDown}
        data-testid="progress-bar"
      >
        {nodes === 2 && (
          <>
            <div
              className={`${vertical ? 'w-4' : 'h-4'} rounded-full`}
              style={vertical ? { height: `${preProgress}%` } : { width: `${preProgress}%` }}
            />

            <div className="relative flex h-0 w-0 items-center justify-center">
              <button
                className={`z-10 ${
                  vertical ? '' : '-ml-2 p-1'
                } hover:bg-tv rounded-full border-1 border-solid border-primary-cloud bg-white-100 p-[6px] shadow-sm`}
              />
            </div>
          </>
        )}
        <div
          className={`bg-${strokeColor} ${vertical ? 'w-4' : 'h-4'} rounded-full`}
          style={vertical ? { height: `${progress - preProgress}%` } : { width: `${progress - preProgress}%` }}
        />
        <div className="relative flex h-0 w-0 flex-col items-center justify-center">
          <button
            className={`z-10 ${
              vertical ? '' : '-ml-2 p-1'
            } hover:bg-tv absolute rounded-full border-1 border-solid border-primary-cloud bg-white-100 p-[6px] shadow-sm`}
          />
        </div>
      </div>
      <div className="flex w-full justify-between bg-transparent">
        <span
          className="flex min-h-fit min-w-fit justify-end whitespace-nowrap text-sm"
          style={vertical ? { height: `${preProgress}%` } : { width: `${preProgress}%` }}
        >
          {startLabel}
        </span>
        <span
          className="flex min-h-fit min-w-fit justify-start whitespace-nowrap  text-sm"
          style={vertical ? { height: `${100 - progress}%` } : { width: `${100 - progress}%` }}
        >
          {endLabel}
        </span>
      </div>
    </MouseMoveHandler>
  );
};

export default ProgressBar;
